import { useCallback, useEffect, useState } from "react";
import type { BatchAuditBatchViewV1 } from "@enrein/shared-contracts/contracts/batch-audit";
import { BATCH_AUDIT_OPPORTUNITY_LABEL_DISPLAY } from "@enrein/shared-contracts";
import type { StudioApiClient } from "./api.js";

export interface BatchAuditPanelProps {
  readonly api: StudioApiClient;
}

export function BatchAuditPanel({ api }: BatchAuditPanelProps) {
  const [urlInput, setUrlInput] = useState("");
  const [selectedBatch, setSelectedBatch] = useState<BatchAuditBatchViewV1 | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refreshBatch = useCallback(
    async (batchId: string) => {
      setLoading(true);
      setError(null);
      try {
        const batch = await api.getBatchAudit(batchId);
        setSelectedBatch(batch);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : String(caught));
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    void (async () => {
      try {
        const list = await api.listBatchAudits();
        const first = list.batches[0];
        if (first) {
          await refreshBatch(first.batchId);
        }
      } catch {
        /* empty state */
      }
    })();
  }, [api, refreshBatch]);

  const submit = async () => {
    const urls = urlInput
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    if (urls.length === 0) return;

    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      const accepted = await api.uploadBatchAudit(urls);
      setMessage(`Batch ${accepted.batchId} submitted (${accepted.submittedCount} URL(s)).`);
      setUrlInput("");
      for (let attempt = 0; attempt < 30; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 150));
        const batch = await api.getBatchAudit(accepted.batchId);
        setSelectedBatch(batch);
        if (batch.status === "COMPLETE" || batch.status === "FAILED") {
          break;
        }
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="batch-audit-panel">
      <div className="panel-header">
        <h3>Batch URL audit</h3>
      </div>
      <p className="subcopy">Upload one URL per line. Results are ranked and labeled for agency outreach (draft signals only).</p>
      <label>
        URLs
        <textarea
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          rows={4}
          placeholder={"https://client-one.com\nhttps://client-two.com"}
        />
      </label>
      <button type="button" onClick={() => void submit()} disabled={loading || urlInput.trim().length === 0}>
        Run batch audit
      </button>
      {message ? <p className="success">{message}</p> : null}
      {error ? <p className="error">{error}</p> : null}
      {selectedBatch ? (
        <>
          <p>
            Batch <strong>{selectedBatch.batchId}</strong> — {selectedBatch.status} ({selectedBatch.completedCount}/
            {selectedBatch.submittedCount})
          </p>
          <ul className="list">
            {selectedBatch.items.map((item) => {
              const labelMeta = item.opportunityLabel
                ? BATCH_AUDIT_OPPORTUNITY_LABEL_DISPLAY[item.opportunityLabel]
                : null;
              return (
                <li key={item.itemId}>
                  <span className="list-primary">{item.submittedUrl}</span>
                  {item.rank ? <span className="status-pill">#{item.rank}</span> : null}
                  {labelMeta ? <span className="status-pill">{labelMeta.label}</span> : null}
                  {item.score !== undefined ? <span className="status-pill">{item.score}</span> : null}
                  {item.failureReason ? <span className="muted">{item.failureReason}</span> : null}
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <p className="muted">No batch audits yet.</p>
      )}
    </div>
  );
}
